import React, { useCallback, useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Image, Button } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { Trash } from 'react-feather';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  aiProcessScores,
  resetAiProcessScores,
  aiAutoProcessScores,
  resetAiAutoProcessScores,
  processBubbleSheet,
  resetProcessBubbleSheet,
} from '../../actions/score';

const img = require('../../assets/images/no_image.png');

const getColor = (props) => {
  if (props.isDragAccept) {
    return '#00e676';
  }
  if (props.isDragReject) {
    return '#ff1744';
  }
  if (props.isFocused) {
    return '#2196f3';
  }
  return '#eeeeee';
};

const Dropzone = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 1px;
  border-radius: 4px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: black;
  outline: none;
  transition: border 0.24s ease-in-out;
`;

const DropzoneTitle = styled.div`
  font-weight: bold;
  font-size: 14px;
`;

const DropzoneSubtitle = styled.div`
  font-style: italic;
`;

const RemoveFileButton = styled.button`
  border: none;
  background: none;
`;

const FilesContainer = styled(Col)`
  display: flex;
  gap: 10px;
  flex-direction: column;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const AIScoreImportModal = ({
  id,
  autoIdentify,
  bubbleSheet,
  downloadTemplateLoading,
  handleTemplateClick,
  isLoading,
  onHide,
}) => {
  const [files, setFiles] = useState([]);

  const dispatch = useDispatch();
  const aiProcessScoresLoading = useSelector(
    (state) => state.score.ongoingRequest.aiProcessScores,
  );
  const aiProcessScoresSuccess = useSelector(
    (state) => state.score.aiProcessScoresSuccess,
  );
  const aiAutoProcessScoresLoading = useSelector(
    (state) => state.score.ongoingRequest.aiAutoProcessScores,
  );
  const aiAutoProcessScoresSuccess = useSelector(
    (state) => state.score.aiAutoProcessScoresSuccess,
  );
  const processBubbleSheetLoading = useSelector(
    (state) => state.score.ongoingRequest.processBubbleSheet,
  );
  const processBubbleSheetSuccess = useSelector(
    (state) => state.score.processBubbleSheetSuccess,
  );

  let loading = aiProcessScoresLoading;
  let success = aiProcessScoresSuccess;
  if (bubbleSheet) {
    loading = processBubbleSheetLoading;
    success = processBubbleSheetSuccess;
  } else if (autoIdentify) {
    loading = aiAutoProcessScoresLoading;
    success = aiAutoProcessScoresSuccess;
  }

  useEffect(() => {
    if (success) {
      if (bubbleSheet) {
        dispatch(resetProcessBubbleSheet());
      } else if (autoIdentify) {
        dispatch(resetAiAutoProcessScores());
      } else {
        dispatch(resetAiProcessScores());
      }
      onHide();
    }
  }, [success, onHide, dispatch, autoIdentify, bubbleSheet]);

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) => ({
      id: crypto.randomUUID(),
      file,
    }));

    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleRemoveFile = useCallback((fileId) => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  }, []);

  const handleSave = () => {
    if (files.length === 0) return;

    const images = files.map((file) => file.file);
    if (bubbleSheet) {
      dispatch(processBubbleSheet({ id, body: { images } }));
    } else if (autoIdentify) {
      dispatch(aiAutoProcessScores({ body: { images } }));
    } else {
      dispatch(aiProcessScores({ id, body: { images } }));
    }
  };

  const renderFiles = files.map((file) => {
    return (
      <div
        className="d-flex justify-content-between align-items-center"
        key={file.id}
      >
        <div>{file.file.name}</div>
        <div>
          <RemoveFileButton
            type="button"
            onClick={() => handleRemoveFile(file.id)}
          >
            <Trash size={20} color="red" />
          </RemoveFileButton>
        </div>
      </div>
    );
  });

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({ onDrop, multiple: true, accept: { 'image/*': [] } });

  return (
    <Container style={{ position: 'relative' }}>
      {loading && (
        <LoadingOverlay>
          <Spinner animation="border" variant="light" />
        </LoadingOverlay>
      )}
      <Row>
        <Col className="d-flex align-items-center">
          <Dropzone
            {...getRootProps({ isFocused, isDragAccept, isDragReject })}
          >
            <input {...getInputProps()} />
            <div className="d-flex flex-column justify-content-center align-items-center">
              <Image src={img} width="50" height="50" className="mb-1" />
              <DropzoneTitle>Puedes arrastrar o cargar imágenes</DropzoneTitle>
            </div>
            <DropzoneSubtitle>(.jpg, .png)</DropzoneSubtitle>
          </Dropzone>
        </Col>
      </Row>
      <Row className="my-2">
        <FilesContainer>{renderFiles}</FilesContainer>
      </Row>
      <Row className="mt-2">
        <Col className="d-flex justify-content-between">
          <Button onClick={open} variant="outline-primary">
            Cargar Imágenes
          </Button>

          <Button
            variant="primary"
            onClick={handleSave}
            disabled={files.length === 0 || loading}
          >
            Guardar
          </Button>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <div className="d-flex">
            <button
              type="button"
              className={`downloadButton p-0 button-with-loader ${
                downloadTemplateLoading && 'loading'
              } justify-content-center align-items-center`}
              disabled={isLoading || downloadTemplateLoading}
              onClick={handleTemplateClick}
            >
              {!autoIdentify && !bubbleSheet && (
                <span className="text">Descargar plantilla</span>
              )}
              <Spinner
                className="spinner ml-auto mr-auto"
                animation="border"
                variant="danger"
                size="sm"
              />
            </button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default AIScoreImportModal;
